import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import styled from 'styled-components'

import {
  SplitDepositSettingType,
  useCombinedPerformanceQuery,
  usePresentInvestmentsTotalsQueryQuery,
} from 'generated/types'
import { routes } from 'router/routes'
import theme from 'theme'
import { USDFormatterAutoRound } from 'utils/number/currency'
import { SIDEBAR_CONTAINER } from 'utils/testing/testIDs'

import { EarnExtensionCard } from './EarnExtensionCards/EarnExtensionCard'
import { SideBarLink } from './SideBarLink'
import {
  AcornsLogoContainer,
  CUSTOM_SMALL_DESKTOP,
  StyledAcornsLogo,
} from './styled-components'
import { calculateBankingHubValue } from './utils'

const Container = styled.div`
  align-items: flex-start;
  border-right: 1px solid ${theme.colors.ivory};
  bottom: 0px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  left: 0px;
  overflow: scroll;
  padding: 0px 20px;
  position: fixed;
  width: 320px;

  @media (max-width: ${CUSTOM_SMALL_DESKTOP}px) {
    width: 230px;
  }

  @media (max-width: ${theme.breakpoints.md}px) {
    width: 94px;
  }

  @media (max-height: ${theme.breakpoints.sm}px) {
    overflow-y: scroll;
  }
`

const NoStyledUl = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ProfileAndSettingLi = styled.li`
  align-self: flex-end;
  margin-top: auto;

  @media (max-width: ${theme.breakpoints.md - 1}px) {
    width: 54px;
  }
  @media (min-width: ${CUSTOM_SMALL_DESKTOP}px) {
    width: 280px;
  }
`

export enum BaseRoutes {
  PRESENT = 'present',
  INVESTING = 'investing-hub',
  INVEST = 'invest',
  LATER = 'later',
  EARLY = 'early',
  BANKING = 'banking-hub',
  CHECKING = 'spend',
  GROW = 'grow',
  EARN = 'earn',
  LEARN = 'learn-hub',
  SETTINGS = 'settings',
}

export const SideBar = () => {
  const { data: combinedPerformanceData } = useCombinedPerformanceQuery({
    variables: { days: -1 },
  })
  const { data: investmentsTotalData } = usePresentInvestmentsTotalsQueryQuery({
    variables: {
      settingTypes: [
        SplitDepositSettingType.DirectDeposit,
        SplitDepositSettingType.DirectDepositIntent,
      ],
    },
  })

  const availableBalance = calculateBankingHubValue(investmentsTotalData)

  const productList = [
    {
      icon: AcornsIcon.Icon.UtilityHomeFv,
      title: 'Home',
      link: routes.presentHome,
      baseRoutes: [BaseRoutes.PRESENT],
    },
    {
      icon: AcornsIcon.Icon.ProductInvestHubFv,
      title: 'Investing',
      link: routes.investingHub.index,
      balance: combinedPerformanceData?.combinedPerformanceByUserId
        ?.currentBalance?.value
        ? USDFormatterAutoRound(
            combinedPerformanceData?.combinedPerformanceByUserId?.currentBalance
              ?.value,
          )
        : '',
      baseRoutes: [
        BaseRoutes.INVESTING,
        BaseRoutes.INVEST,
        BaseRoutes.LATER,
        BaseRoutes.EARLY,
      ],
    },
    {
      icon: AcornsIcon.Icon.UtilityBankFv,
      title: 'Banking',
      link: routes.bankingHub.index,
      baseRoutes: [BaseRoutes.BANKING, BaseRoutes.CHECKING],
      balance: availableBalance,
    },
    {
      icon: AcornsIcon.Icon.ProductGrowFv,
      title: 'Learning',
      link: routes.learn.index,
      baseRoutes: [BaseRoutes.LEARN],
    },
    {
      icon: AcornsIcon.Icon.ProductEarnFv,
      title: 'Earning',
      link: routes.earn.index,
      baseRoutes: [BaseRoutes.EARN],
    },
  ]

  return (
    <Container data-testid={SIDEBAR_CONTAINER}>
      <AcornsLogoContainer>
        <StyledAcornsLogo link={routes.presentHome} width={27} />
      </AcornsLogoContainer>
      <NoStyledUl>
        {productList.map(
          ({ balance, baseRoutes, icon, link, title }, index) => (
            <li key={title}>
              <SideBarLink
                balance={balance}
                baseRoutes={baseRoutes}
                icon={icon}
                index={index}
                link={link}
                title={title}
              />
            </li>
          ),
        )}
        <li>
          <EarnExtensionCard />
        </li>
        <ProfileAndSettingLi className="profile-settings">
          <SideBarLink
            baseRoutes={[BaseRoutes.SETTINGS]}
            icon={AcornsIcon.Icon.UtilityMenuFv}
            index={productList.length}
            key="Profile & Settings"
            link={routes.settings.index}
            title="Profile & Settings"
            isBottomElement={true}
          />
        </ProfileAndSettingLi>
      </NoStyledUl>
    </Container>
  )
}
